
import React, { useEffect, useRef, useState } from "react"
import { SpinnerText } from "../../ui/Spinner"
import { searchTerms } from "../../../services/euipoApi"

interface SearchTerm {
  text: string
  classNumber?: number
  conceptId?: string
}

interface SearchBarProps {
  onResults?: (results: SearchTerm[]) => void
}

export default function SearchBar({ onResults }: SearchBarProps) {
  const [query, setQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resultCount, setResultCount] = useState<number | null>(null)

  const requestId = useRef(0)
  const onResultsRef = useRef(onResults)

  useEffect(() => {
    onResultsRef.current = onResults
  }, [onResults])

  useEffect(() => {
    const trimmed = query.trim()
    const currentRequest = ++requestId.current

    if (trimmed.length < 2) {
      setLoading(false)
      setError(null)
      setResultCount(null)
      onResultsRef.current?.([])
      return
    }

    setLoading(true)
    setError(null)
    setResultCount(null)
    onResultsRef.current?.([])

    const timer = window.setTimeout(async () => {
      try {
        const data = await searchTerms(trimmed)

        if (currentRequest !== requestId.current) return

        let results: SearchTerm[]

        if (Array.isArray(data)) {
          results = data
        } else if (Array.isArray(data?.terms)) {
          results = data.terms
        } else if (Array.isArray(data?.content)) {
          results = data.content
        } else {
          throw new Error("Unexpected EUIPO response format")
        }

        results = results.filter(
          (item): item is SearchTerm =>
            item !== null &&
            typeof item === "object" &&
            typeof item.text === "string"
        )

        onResultsRef.current?.(results)
        setResultCount(results.length)
      } catch (err) {
        if (currentRequest !== requestId.current) return

        console.error("Fehler bei der EUIPO-Suche:", err)
        setError("Error while searching terms.")
        onResultsRef.current?.([])
        setResultCount(null)
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false)
        }
      }
    }, 500)

    return () => {
      window.clearTimeout(timer)
    }
  }, [query])

  return (
    <div>
      <label className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-100">
        <span aria-hidden="true" className="text-slate-400">
          ⌕
        </span>

        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search terms (e.g. "Clothing" or "36")'
          aria-label="Search harmonized terms"
          className="min-w-0 w-full bg-transparent py-2.5 text-sm outline-none"
        />

        {loading && (
          <span
            role="status"
            className="shrink-0 text-xs text-blue-700"
          >
            <SpinnerText>Searching...</SpinnerText>
          </span>
        )}
      </label>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}

      {resultCount === 0 && !loading && !error && (
        <p className="mt-2 text-xs text-slate-500">
          No matching terms found.
        </p>
      )}
    </div>
  )
}
